/**
 * msalConfig.js — Dual-mode MSAL Configuration
 *
 * Supports two SSO provider modes, controlled by REACT_APP_SSO_PROVIDER:
 *
 *   "azuread"  → Standard Microsoft Azure AD / Entra ID
 *                Uses Microsoft cloud-instance discovery (default MSAL behaviour).
 *                Supports Microsoft Graph API (User.Read scope).
 *
 *   "oidc"     → Custom OIDC / OAuth2 provider (e.g. GS1 STS, Okta, Keycloak, etc.)
 *                Disables Microsoft cloud-instance discovery via knownAuthorities.
 *                Only standard OIDC scopes (openid, profile, email) are used.
 *                Microsoft Graph is NOT available in this mode.
 *
 * Required .env variables (all modes):
 *   REACT_APP_SSO_PROVIDER      = "azuread" | "oidc"   (default: "azuread")
 *   REACT_APP_SSO_CLIENT_ID     = your client / app ID
 *   REACT_APP_SSO_AUTHORITY     = authority base URL
 *   REACT_APP_SSO_REDIRECT_URI  = registered redirect URI
 *
 * Azure AD specific (.env example):
 *   REACT_APP_SSO_PROVIDER=azuread
 *   REACT_APP_SSO_CLIENT_ID=xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx
 *   REACT_APP_SSO_AUTHORITY=https://login.microsoftonline.com/<tenant-id>
 *   REACT_APP_SSO_REDIRECT_URI=http://localhost:3000
 *
 * Custom OIDC specific (.env example for GS1):
 *   REACT_APP_SSO_PROVIDER=oidc
 *   REACT_APP_SSO_CLIENT_ID=ACTIVATEUI-DEV
 *   REACT_APP_SSO_AUTHORITY=https://gs1sso-dv2.gs1.org/
 *   REACT_APP_SSO_REDIRECT_URI=http://localhost:3000/callback.html
 */

// ── Provider detection ────────────────────────────────────────────────────────
const SSO_PROVIDER = (
  process.env.REACT_APP_SSO_PROVIDER || 'azuread'
).toLowerCase().trim();

export const isAzureAD = SSO_PROVIDER === 'azuread';
export const isCustomOIDC = SSO_PROVIDER === 'oidc';

// ── Shared config values ──────────────────────────────────────────────────────
const authority =
  process.env.REACT_APP_SSO_AUTHORITY ||
  (isAzureAD
    ? 'https://login.microsoftonline.com/common'
    : 'https://gs1sso-dv2.gs1.org/');

const clientId =
  process.env.REACT_APP_SSO_CLIENT_ID ||
  (isAzureAD ? 'YOUR_AZURE_CLIENT_ID' : 'ACTIVATEUI-DEV');

const redirectUri =
  process.env.REACT_APP_SSO_REDIRECT_URI ||
  (isAzureAD ? window.location.origin : `${window.location.origin}/callback.html`);

const postLogoutRedirectUri =
  process.env.REACT_APP_SSO_POST_LOGOUT_URI ||
  window.location.origin;

// ── knownAuthorities (only needed for non-Microsoft OIDC providers) ───────────
// For Azure AD this must be empty — MSAL handles discovery automatically.
// For custom OIDC providers, list the authority hostname to bypass Microsoft's
// cloud-instance discovery (which would fail with "invalid_instance").
let knownAuthorities = [];
if (isCustomOIDC) {
  try {
    knownAuthorities = [new URL(authority).hostname];
  } catch {
    console.warn('[msalConfig] Could not parse authority URL:', authority);
  }
}

// ── authorityMetadata (custom OIDC only) ──────────────────────────────────────
// Providing the OIDC metadata inline prevents MSAL from fetching
// /.well-known/openid-configuration from the browser, which would fail with
// a CORS error because GS1's STS does not include CORS headers on that endpoint.
//
// These values are taken directly from:
//   https://gs1sso-dv2.gs1.org/.well-known/openid-configuration
// Update this object if GS1 rotates or changes their endpoints.
const gs1AuthorityMetadata = JSON.stringify({
  issuer: 'https://gs1sso-dv2.gs1.org',
  authorization_endpoint: 'https://gs1sso-dv2.gs1.org/connect/authorize',
  token_endpoint: 'https://gs1sso-dv2.gs1.org/connect/token',
  userinfo_endpoint: 'https://gs1sso-dv2.gs1.org/connect/userinfo',
  end_session_endpoint: 'https://gs1sso-dv2.gs1.org/connect/endsession',
  jwks_uri: 'https://gs1sso-dv2.gs1.org/.well-known/openid-configuration/jwks',
  response_types_supported: ['code', 'id_token', 'code id_token'],
  response_modes_supported: ['form_post', 'query', 'fragment'],
  subject_types_supported: ['public'],
  id_token_signing_alg_values_supported: ['RS256'],
  code_challenge_methods_supported: ['plain', 'S256'],
  scopes_supported: ['openid', 'profile', 'email', 'offline_access'],
  claims_supported: [
    'sub', 'name', 'family_name', 'given_name', 'preferred_username',
    'email', 'picture', 'locale', 'updated_at',
  ],
});

// ── MSAL configuration object ─────────────────────────────────────────────────
export const msalConfig = {
  auth: {
    clientId,
    authority,

    /**
     * knownAuthorities:
     *  - Azure AD mode  → [] (empty — MSAL uses Microsoft cloud discovery)
     *  - Custom OIDC    → [hostname] (bypasses Microsoft cloud discovery)
     */
    knownAuthorities,

    /**
     * authorityMetadata (custom OIDC only):
     * Providing the OIDC discovery document inline prevents MSAL from
     * fetching /.well-known/openid-configuration from the browser.
     * GS1's STS does not send CORS headers on that endpoint, so the
     * browser fetch would fail. By supplying the metadata here, MSAL
     * skips the network request entirely.
     *
     * Leave undefined for Azure AD — MSAL handles discovery automatically.
     */
    ...(isCustomOIDC && { authorityMetadata: gs1AuthorityMetadata }),

    redirectUri,
    postLogoutRedirectUri,

    /**
     * navigateToLoginRequestUrl:
     *  - Azure AD  → true  (MSAL navigates back to the page that triggered login)
     *  - Custom OIDC → false (we use a dedicated callback.html page)
     */
    navigateToLoginRequestUrl: isAzureAD,
  },

  cache: {
    cacheLocation: 'sessionStorage', // sessionStorage is safer than localStorage
    storeAuthStateInCookie: false,   // Set true only for IE11 / legacy Edge
  },

  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return; // Never log PII
        if (process.env.NODE_ENV === 'development') {
          console.log(`[MSAL] ${message}`);
        }
      },
    },
  },
};

// ── Login request scopes ──────────────────────────────────────────────────────
/**
 * Login request scopes.
 *
 * Azure AD mode: includes User.Read for Microsoft Graph.
 *
 * Custom OIDC / GS1 mode:
 *   Only 'openid' is used for the initial login redirect.
 *   GS1's STS (ACTIVATEUI-DEV client) may not be authorised for 'profile'
 *   or 'email' scopes — sending them causes invalid_scope.
 *   User info (name, email) is extracted from the ID token claims instead.
 *
 *   If your GS1 client IS authorised for profile/email, change this to:
 *     ['openid', 'profile', 'email']
 */
export const loginRequest = {
  scopes: isAzureAD
    ? ['openid', 'profile', 'email', 'User.Read']
    : ['openid'],
};

// ── Graph / UserInfo endpoints ────────────────────────────────────────────────
export const graphConfig = {
  /**
   * Azure AD: Microsoft Graph /me endpoint for fetching user profile.
   * Custom OIDC: Standard OIDC /userinfo endpoint (adjust path if needed).
   */
  userInfoEndpoint: isAzureAD
    ? 'https://graph.microsoft.com/v1.0/me'
    : `${authority.replace(/\/$/, '')}/connect/userinfo`,
};

// ── Debug helper (development only) ──────────────────────────────────────────
if (process.env.NODE_ENV === 'development') {
  console.log(
    `[msalConfig] SSO provider: ${SSO_PROVIDER} | authority: ${authority} | clientId: ${clientId}`
  );
}
